import { useEffect } from 'react';
import { Form, Input, message, Modal, Select } from 'antd';
import { useEmployeesMock } from '@/entities/employee/api/hooks';
import { useAddMedicalRecord } from '@/entities/medical/api/hooks';
import { useOrgTree } from '@/entities/organization/api/hooks';
import { collectDepartments } from '@/entities/organization/lib/collectDepartments';
import { EXAM_TYPE_OPTIONS } from '@/shared/constants';

export type MedicalPrefill = {
  employeeId: string | number;
  employeeName: string;
  position: string;
  department: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  prefill?: MedicalPrefill | null;
};

export function AddMedicalRecordModal({ open, onClose, prefill }: Props) {
  const addMedical = useAddMedicalRecord();
  const [form] = Form.useForm();
  const { data: allEmployees } = useEmployeesMock();
  const { data: orgNodes } = useOrgTree();

  const positionOptions =
    allEmployees
      ?.map((e) => e.position)
      .filter((p, i, a) => a.indexOf(p) === i)
      .sort() ?? [];
  const positionSelectOptions = positionOptions.map((p) => ({ value: p, label: p }));

  const deptFormOptions = orgNodes ? collectDepartments(orgNodes).map((d) => ({ value: d, label: d })) : [];

  useEffect(() => {
    if (!open) return;
    if (prefill) {
      form.setFieldsValue({
        fio: prefill.employeeName,
        position: prefill.position,
        department: prefill.department,
      });
    } else {
      form.resetFields();
    }
  }, [open, prefill, form]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleOk = () => {
    void form.validateFields().then((values) => {
      addMedical.mutate(
        {
          employeeId: prefill?.employeeId,
          employeeName: values.fio as string,
          position: values.position as string,
          department: values.department as string,
          examType: values.examType as string,
          lastDate: values.date as string,
          nextDate: '—',
          status: 'info',
          result: undefined,
          attachmentName: undefined,
        },
        {
          onSuccess: () => {
            message.success('Запись добавлена');
            form.resetFields();
            onClose();
          },
        },
      );
    });
  };

  return (
    <Modal
      title="Новая запись медосмотра"
      open={open}
      confirmLoading={addMedical.isPending}
      onCancel={handleCancel}
      onOk={handleOk}
      okText="Сохранить"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="fio" label="ФИО" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="position" label="Профессия" rules={[{ required: true }]}>
          <Select showSearch placeholder="Выберите профессию" options={positionSelectOptions} />
        </Form.Item>
        <Form.Item name="department" label="Подразделение" rules={[{ required: true }]}>
          <Select showSearch placeholder="Выберите подразделение" options={deptFormOptions} />
        </Form.Item>
        <Form.Item name="examType" label="Вид осмотра" rules={[{ required: true }]}>
          <Select placeholder="Выберите вид" options={[...EXAM_TYPE_OPTIONS]} />
        </Form.Item>
        <Form.Item name="date" label="Дата" rules={[{ required: true }]}>
          <Input placeholder="ДД.ММ.ГГГГ" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
